import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  DataList,
  DataListAction,
  DataListCell,
  DataListItem,
  DataListItemCells,
  DataListItemRow,
  EmptyState,
  EmptyStateBody,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Modal,
  PageSection,
  Spinner,
  Tab,
  TabTitleText,
  Tabs,
  TextInput,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { PlusCircleIcon } from '@patternfly/react-icons';
import { useAuthedFetch } from 'src/hooks/useAuthedFetch';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface UserIdentity {
  id: string;
  provider: string;
  external_user_id: string;
  email?: string | null;
}

interface User {
  id: string;
  email?: string | null;
  display_name?: string | null;
  status: string;
  sms_phone?: string | null;
  sms_opt_in: boolean;
  identities: UserIdentity[];
}

interface Location {
  id: string;
  name: string;
  address?: string | null;
  timezone?: string | null;
}

interface Robot {
  id: string;
  name?: string | null;
  serial_number?: string | null;
  model?: string | null;
  type: string;
  status?: string | null;
  location_id?: string | null;
}

const defaultUser: Partial<User> = {
  email: '',
  display_name: '',
  status: 'active',
  sms_phone: '',
  sms_opt_in: false,
  identities: [],
};

const defaultLocation: Partial<Location> = {
  name: '',
  address: '',
  timezone: '',
};

const defaultRobot: Partial<Robot> = {
  name: '',
  serial_number: '',
  model: '',
  type: 'robot',
  status: '',
  location_id: '',
};

const CoreEntities: React.FC = () => {
  const authedFetch = useAuthedFetch();
  const [activeTab, setActiveTab] = useState<string | number>('users');

  const [users, setUsers] = useState<User[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [robots, setRobots] = useState<Robot[]>([]);

  const [loading, setLoading] = useState({ users: true, locations: true, robots: true });
  const [error, setError] = useState<string | null>(null);

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [robotModalOpen, setRobotModalOpen] = useState(false);

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [editingRobot, setEditingRobot] = useState<Robot | null>(null);

  const [userDraft, setUserDraft] = useState<Partial<User>>(defaultUser);
  const [locationDraft, setLocationDraft] = useState<Partial<Location>>(defaultLocation);
  const [robotDraft, setRobotDraft] = useState<Partial<Robot>>(defaultRobot);

  const [identityDraft, setIdentityDraft] = useState<Partial<UserIdentity>>({
    provider: '',
    external_user_id: '',
    email: '',
  });
  const [identitySubmitting, setIdentitySubmitting] = useState(false);

  const locationMap = useMemo(() => {
    const map = new Map<string, Location>();
    locations.forEach((location) => map.set(location.id, location));
    return map;
  }, [locations]);

  const copyToClipboard = async (value: string, label: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
      }
      window.prompt(`Copy ${label}:`, value);
    } catch {
      setError(`Unable to copy ${label}.`);
    }
  };

  const loadUsers = useCallback(async () => {
    setLoading((prev) => ({ ...prev, users: true }));
    try {
      const response = await authedFetch(`${API_BASE}/api/core/users`);
      if (!response.ok) {
        throw new Error(`Failed to load users (${response.status})`);
      }
      const data: User[] = await response.json();
      setUsers(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  }, [authedFetch]);

  const loadLocations = useCallback(async () => {
    setLoading((prev) => ({ ...prev, locations: true }));
    try {
      const response = await authedFetch(`${API_BASE}/api/core/locations`);
      if (!response.ok) {
        throw new Error(`Failed to load locations (${response.status})`);
      }
      const data: Location[] = await response.json();
      setLocations(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading((prev) => ({ ...prev, locations: false }));
    }
  }, [authedFetch]);

  const loadRobots = useCallback(async () => {
    setLoading((prev) => ({ ...prev, robots: true }));
    try {
      const response = await authedFetch(`${API_BASE}/api/core/robots`);
      if (!response.ok) {
        throw new Error(`Failed to load robots (${response.status})`);
      }
      const data: Robot[] = await response.json();
      setRobots(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading((prev) => ({ ...prev, robots: false }));
    }
  }, [authedFetch]);

  useEffect(() => {
    setError(null);
    void loadUsers();
    void loadLocations();
    void loadRobots();
  }, [loadLocations, loadRobots, loadUsers]);

  const openUserModal = (user?: User) => {
    setEditingUser(user ?? null);
    setUserDraft(user ? { ...user } : { ...defaultUser });
    setUserModalOpen(true);
  };

  const openLocationModal = (location?: Location) => {
    setEditingLocation(location ?? null);
    setLocationDraft(location ? { ...location } : { ...defaultLocation });
    setLocationModalOpen(true);
  };

  const openRobotModal = (robot?: Robot) => {
    setEditingRobot(robot ?? null);
    setRobotDraft(robot ? { ...robot } : { ...defaultRobot });
    setRobotModalOpen(true);
  };

  const closeModals = () => {
    setUserModalOpen(false);
    setLocationModalOpen(false);
    setRobotModalOpen(false);
    setEditingUser(null);
    setEditingLocation(null);
    setEditingRobot(null);
  };

  const saveUser = async () => {
    try {
      const payload = {
        email: userDraft.email || null,
        display_name: userDraft.display_name || null,
        status: userDraft.status || 'active',
        sms_phone: userDraft.sms_phone || null,
        sms_opt_in: userDraft.sms_opt_in || false,
        identities: userDraft.identities?.length ? userDraft.identities : undefined,
      };
      const isEdit = Boolean(editingUser);
      const url = isEdit
        ? `${API_BASE}/api/core/users/${editingUser?.id}`
        : `${API_BASE}/api/core/users`;
      const method = isEdit ? 'PUT' : 'POST';
      const response = await authedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Unable to save user (${response.status})`);
      }
      await loadUsers();
      closeModals();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const saveLocation = async () => {
    try {
      if (!locationDraft.name) {
        throw new Error('Location name is required');
      }
      const payload = {
        name: locationDraft.name,
        address: locationDraft.address || null,
        timezone: locationDraft.timezone || null,
      };
      const isEdit = Boolean(editingLocation);
      const url = isEdit
        ? `${API_BASE}/api/core/locations/${editingLocation?.id}`
        : `${API_BASE}/api/core/locations`;
      const method = isEdit ? 'PUT' : 'POST';
      const response = await authedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Unable to save location (${response.status})`);
      }
      await loadLocations();
      closeModals();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const saveRobot = async () => {
    try {
      const payload = {
        name: robotDraft.name || null,
        serial_number: robotDraft.serial_number || null,
        model: robotDraft.model || null,
        type: robotDraft.type || 'robot',
        status: robotDraft.status || null,
        location_id: robotDraft.location_id || null,
      };
      const isEdit = Boolean(editingRobot);
      const url = isEdit
        ? `${API_BASE}/api/core/robots/${editingRobot?.id}`
        : `${API_BASE}/api/core/robots`;
      const method = isEdit ? 'PUT' : 'POST';
      const response = await authedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Unable to save robot (${response.status})`);
      }
      await loadRobots();
      closeModals();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      const response = await authedFetch(`${API_BASE}/api/core/users/${userId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`Unable to delete user (${response.status})`);
      }
      await loadUsers();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deleteLocation = async (locationId: string) => {
    try {
      const response = await authedFetch(`${API_BASE}/api/core/locations/${locationId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`Unable to delete location (${response.status})`);
      }
      await loadLocations();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deleteRobot = async (robotId: string) => {
    try {
      const response = await authedFetch(`${API_BASE}/api/core/robots/${robotId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`Unable to delete robot (${response.status})`);
      }
      await loadRobots();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const submitIdentity = async () => {
    if (!editingUser) {
      return;
    }
    if (!identityDraft.provider || !identityDraft.external_user_id) {
      setError('Provider and external user id are required');
      return;
    }
    setIdentitySubmitting(true);
    try {
      const response = await authedFetch(
        `${API_BASE}/api/core/users/${editingUser.id}/identities`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            provider: identityDraft.provider,
            external_user_id: identityDraft.external_user_id,
            email: identityDraft.email || null,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(`Unable to add identity (${response.status})`);
      }
      setIdentityDraft({ provider: '', external_user_id: '', email: '' });
      await loadUsers();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIdentitySubmitting(false);
    }
  };

  const renderEmpty = (title: string, body: string) => (
    <EmptyState>
      <Title headingLevel="h4">{title}</Title>
      <EmptyStateBody>{body}</EmptyStateBody>
    </EmptyState>
  );

  const renderError = error ? (
    <Alert variant="danger" title="Something went wrong" isInline>
      {error}
    </Alert>
  ) : null;

  return (
    <PageSection isFilled>
      <Card isFullHeight>
        <CardTitle>
          <Title headingLevel="h2">Core Management</Title>
        </CardTitle>
        <CardBody>
          {renderError}
          <Tabs
            activeKey={activeTab}
            onSelect={(_, key) => setActiveTab(key)}
            aria-label="Core entities"
          >
            <Tab
              eventKey="users"
              title={<TabTitleText>Users</TabTitleText>}
            >
              <Toolbar>
                <ToolbarContent>
                  <ToolbarItem>
                    <Button icon={<PlusCircleIcon />} onClick={() => openUserModal()}>
                      New user
                    </Button>
                  </ToolbarItem>
                </ToolbarContent>
              </Toolbar>
              {loading.users ? (
                <Spinner size="lg" />
              ) : users.length === 0 ? (
                renderEmpty('No users', 'Create the first user identity.')
              ) : (
                <DataList aria-label="Users list" isCompact>
                  {users.map((user) => (
                    <DataListItem key={user.id} aria-labelledby={`user-${user.id}`}>
                      <DataListItemRow>
                        <DataListItemCells
                          dataListCells={[
                            <DataListCell key="primary">
                              <Title headingLevel="h4" id={`user-${user.id}`}>
                                {user.display_name || user.email || 'Unnamed user'}
                              </Title>
                              <div style={{ color: 'var(--pf-t--global--color--nonstatus--gray--default)' }}>
                                {user.email || 'No email'}
                              </div>
                            </DataListCell>,
                            <DataListCell key="status">
                              <div>Status: {user.status}</div>
                              <div>
                                SMS: {user.sms_phone ? `${user.sms_phone} (${user.sms_opt_in ? 'opt-in' : 'off'})` : '—'}
                              </div>
                            </DataListCell>,
                            <DataListCell key="identity">Identities: {user.identities?.length ?? 0}</DataListCell>,
                          ]}
                        />
                        <DataListAction
                          id={`user-${user.id}-actions`}
                          aria-label="User actions"
                          aria-labelledby={`user-${user.id}`}
                        >
                          <Button variant="secondary" onClick={() => openUserModal(user)}>
                            Edit
                          </Button>
                          <Button variant="link" isDanger onClick={() => deleteUser(user.id)}>
                            Delete
                          </Button>
                        </DataListAction>
                      </DataListItemRow>
                    </DataListItem>
                  ))}
                </DataList>
              )}
            </Tab>
            <Tab
              eventKey="locations"
              title={<TabTitleText>Locations</TabTitleText>}
            >
              <Toolbar>
                <ToolbarContent>
                  <ToolbarItem>
                    <Button icon={<PlusCircleIcon />} onClick={() => openLocationModal()}>
                      New location
                    </Button>
                  </ToolbarItem>
                </ToolbarContent>
              </Toolbar>
              {loading.locations ? (
                <Spinner size="lg" />
              ) : locations.length === 0 ? (
                renderEmpty('No locations', 'Add a location to start assigning robots.')
              ) : (
                <DataList aria-label="Locations list" isCompact>
                  {locations.map((location) => (
                    <DataListItem key={location.id} aria-labelledby={`location-${location.id}`}>
                      <DataListItemRow>
                        <DataListItemCells
                          dataListCells={[
                            <DataListCell key="primary">
                              <Title headingLevel="h4" id={`location-${location.id}`}>
                                {location.name}
                              </Title>
                              <div style={{ color: 'var(--pf-t--global--color--nonstatus--gray--default)' }}>
                                {location.address || 'No address'}
                              </div>
                            </DataListCell>,
                            <DataListCell key="timezone">Timezone: {location.timezone || '—'}</DataListCell>,
                          ]}
                        />
                        <DataListAction
                          id={`location-${location.id}-actions`}
                          aria-label="Location actions"
                          aria-labelledby={`location-${location.id}`}
                        >
                          <Button variant="secondary" onClick={() => openLocationModal(location)}>
                            Edit
                          </Button>
                          <Button variant="link" isDanger onClick={() => deleteLocation(location.id)}>
                            Delete
                          </Button>
                        </DataListAction>
                      </DataListItemRow>
                    </DataListItem>
                  ))}
                </DataList>
              )}
            </Tab>
            <Tab
              eventKey="robots"
              title={<TabTitleText>Robots</TabTitleText>}
            >
              <Toolbar>
                <ToolbarContent>
                  <ToolbarItem>
                    <Button icon={<PlusCircleIcon />} onClick={() => openRobotModal()}>
                      New robot
                    </Button>
                  </ToolbarItem>
                </ToolbarContent>
              </Toolbar>
              {loading.robots ? (
                <Spinner size="lg" />
              ) : robots.length === 0 ? (
                renderEmpty('No robots', 'Register your first robot to start.')
              ) : (
                <DataList aria-label="Robots list" isCompact>
                  {robots.map((robot) => (
                    <DataListItem key={robot.id} aria-labelledby={`robot-${robot.id}`}>
                      <DataListItemRow>
                        <DataListItemCells
                          dataListCells={[
                            <DataListCell key="primary">
                              <Title headingLevel="h4" id={`robot-${robot.id}`}>
                                {robot.name || robot.serial_number || 'Unnamed robot'}
                              </Title>
                              <div style={{ color: 'var(--pf-t--global--color--nonstatus--gray--default)' }}>
                                {robot.model || 'No model'} · {robot.type}
                              </div>
                              <div style={{ marginTop: '4px', fontSize: '0.85rem' }}>
                                ID: <code>{robot.id}</code>{' '}
                                <Button
                                  variant="link"
                                  isInline
                                  onClick={() => copyToClipboard(robot.id, 'robot id')}
                                >
                                  Copy
                                </Button>
                              </div>
                            </DataListCell>,
                            <DataListCell key="status">Status: {robot.status || '—'}</DataListCell>,
                            <DataListCell key="location">
                              Location: {robot.location_id ? locationMap.get(robot.location_id)?.name || robot.location_id : '—'}
                            </DataListCell>,
                          ]}
                        />
                        <DataListAction
                          id={`robot-${robot.id}-actions`}
                          aria-label="Robot actions"
                          aria-labelledby={`robot-${robot.id}`}
                        >
                          <Button variant="secondary" onClick={() => openRobotModal(robot)}>
                            Edit
                          </Button>
                          <Button variant="link" isDanger onClick={() => deleteRobot(robot.id)}>
                            Delete
                          </Button>
                        </DataListAction>
                      </DataListItemRow>
                    </DataListItem>
                  ))}
                </DataList>
              )}
            </Tab>
          </Tabs>
        </CardBody>
      </Card>

      <Modal
        title={editingUser ? 'Edit user' : 'Create user'}
        isOpen={userModalOpen}
        onClose={closeModals}
        variant="medium"
      >
        <Form>
          <FormGroup label="Email" fieldId="user-email">
            <TextInput
              id="user-email"
              value={userDraft.email || ''}
              onChange={(_, value) => setUserDraft((prev) => ({ ...prev, email: value }))}
            />
          </FormGroup>
          <FormGroup label="Display name" fieldId="user-name">
            <TextInput
              id="user-name"
              value={userDraft.display_name || ''}
              onChange={(_, value) => setUserDraft((prev) => ({ ...prev, display_name: value }))}
            />
          </FormGroup>
          <FormGroup label="Status" fieldId="user-status">
            <TextInput
              id="user-status"
              value={userDraft.status || 'active'}
              onChange={(_, value) => setUserDraft((prev) => ({ ...prev, status: value }))}
            />
          </FormGroup>
          <FormGroup label="SMS phone" fieldId="user-sms">
            <TextInput
              id="user-sms"
              value={userDraft.sms_phone || ''}
              onChange={(_, value) => setUserDraft((prev) => ({ ...prev, sms_phone: value }))}
            />
          </FormGroup>
          <FormGroup label="SMS opt-in" fieldId="user-sms-opt">
            <FormSelect
              id="user-sms-opt"
              value={userDraft.sms_opt_in ? 'true' : 'false'}
              onChange={(_, value) => setUserDraft((prev) => ({ ...prev, sms_opt_in: value === 'true' }))}
            >
              <FormSelectOption value="false" label="No" />
              <FormSelectOption value="true" label="Yes" />
            </FormSelect>
          </FormGroup>
          {editingUser && (
            <>
              <Title headingLevel="h4" style={{ marginTop: '1rem' }}>Add identity</Title>
              <FormGroup label="Provider" fieldId="identity-provider">
                <TextInput
                  id="identity-provider"
                  value={identityDraft.provider || ''}
                  onChange={(_, value) => setIdentityDraft((prev) => ({ ...prev, provider: value }))}
                />
              </FormGroup>
              <FormGroup label="External user id" fieldId="identity-external">
                <TextInput
                  id="identity-external"
                  value={identityDraft.external_user_id || ''}
                  onChange={(_, value) => setIdentityDraft((prev) => ({ ...prev, external_user_id: value }))}
                />
              </FormGroup>
              <FormGroup label="Identity email" fieldId="identity-email">
                <TextInput
                  id="identity-email"
                  value={identityDraft.email || ''}
                  onChange={(_, value) => setIdentityDraft((prev) => ({ ...prev, email: value }))}
                />
              </FormGroup>
              <Button
                variant="secondary"
                isDisabled={identitySubmitting}
                onClick={submitIdentity}
              >
                Add identity
              </Button>
            </>
          )}
        </Form>
        <Toolbar style={{ marginTop: '1.5rem' }}>
          <ToolbarContent>
            <ToolbarItem>
              <Button onClick={saveUser}>Save</Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button variant="link" onClick={closeModals}>
                Cancel
              </Button>
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>
      </Modal>

      <Modal
        title={editingLocation ? 'Edit location' : 'Create location'}
        isOpen={locationModalOpen}
        onClose={closeModals}
        variant="medium"
      >
        <Form>
          <FormGroup label="Name" fieldId="location-name" isRequired>
            <TextInput
              id="location-name"
              value={locationDraft.name || ''}
              onChange={(_, value) => setLocationDraft((prev) => ({ ...prev, name: value }))}
            />
          </FormGroup>
          <FormGroup label="Address" fieldId="location-address">
            <TextInput
              id="location-address"
              value={locationDraft.address || ''}
              onChange={(_, value) => setLocationDraft((prev) => ({ ...prev, address: value }))}
            />
          </FormGroup>
          <FormGroup label="Timezone" fieldId="location-timezone">
            <TextInput
              id="location-timezone"
              value={locationDraft.timezone || ''}
              onChange={(_, value) => setLocationDraft((prev) => ({ ...prev, timezone: value }))}
            />
          </FormGroup>
        </Form>
        <Toolbar style={{ marginTop: '1.5rem' }}>
          <ToolbarContent>
            <ToolbarItem>
              <Button onClick={saveLocation}>Save</Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button variant="link" onClick={closeModals}>
                Cancel
              </Button>
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>
      </Modal>

      <Modal
        title={editingRobot ? 'Edit robot' : 'Create robot'}
        isOpen={robotModalOpen}
        onClose={closeModals}
        variant="medium"
      >
        <Form>
          <FormGroup label="Name" fieldId="robot-name">
            <TextInput
              id="robot-name"
              value={robotDraft.name || ''}
              onChange={(_, value) => setRobotDraft((prev) => ({ ...prev, name: value }))}
            />
          </FormGroup>
          <FormGroup label="Serial" fieldId="robot-serial">
            <TextInput
              id="robot-serial"
              value={robotDraft.serial_number || ''}
              onChange={(_, value) => setRobotDraft((prev) => ({ ...prev, serial_number: value }))}
            />
          </FormGroup>
          <FormGroup label="Model" fieldId="robot-model">
            <TextInput
              id="robot-model"
              value={robotDraft.model || ''}
              onChange={(_, value) => setRobotDraft((prev) => ({ ...prev, model: value }))}
            />
          </FormGroup>
          <FormGroup label="Type" fieldId="robot-type">
            <TextInput
              id="robot-type"
              value={robotDraft.type || 'robot'}
              onChange={(_, value) => setRobotDraft((prev) => ({ ...prev, type: value }))}
            />
          </FormGroup>
          <FormGroup label="Status" fieldId="robot-status">
            <TextInput
              id="robot-status"
              value={robotDraft.status || ''}
              onChange={(_, value) => setRobotDraft((prev) => ({ ...prev, status: value }))}
            />
          </FormGroup>
          <FormGroup label="Location" fieldId="robot-location">
            <FormSelect
              id="robot-location"
              value={robotDraft.location_id || ''}
              onChange={(_, value) =>
                setRobotDraft((prev) => ({ ...prev, location_id: value || '' }))
              }
            >
              <FormSelectOption value="" label="No location" />
              {locations.map((location) => (
                <FormSelectOption key={location.id} value={location.id} label={location.name} />
              ))}
            </FormSelect>
          </FormGroup>
        </Form>
        <Toolbar style={{ marginTop: '1.5rem' }}>
          <ToolbarContent>
            <ToolbarItem>
              <Button onClick={saveRobot}>Save</Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button variant="link" onClick={closeModals}>
                Cancel
              </Button>
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>
      </Modal>
    </PageSection>
  );
};

export default CoreEntities;
